<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import { useHardalEvents } from '../composables/useHardalEvents'
import { useKeyEvents } from '../composables/useKeyEvents'
import { useKeywordArea } from '../composables/useKeywordArea'
import { useProductCustomization } from '../composables/useProductCustomization'
import { useSourceMedium } from '../composables/useSourceMedium'
import { GA4_KEY_EVENTS, MMP_KEY_EVENTS } from '../configs/conversions'
import { productFeedSource } from '../configs/feedSources'
import { sourceMediumPlatformDescriptors } from '../configs/platforms'
import { registerDefinitionsMessages } from '../i18n'
import type { KeyEventAccount } from '../models/conversion.types'
import type { FeedSourceDescriptor } from '../models/product.types'
import { resolveConnectionFollowups, type ConnectionFollowup } from '../service/connectionFollowups'

import HardalKeyEventModal from './conversions/HardalKeyEventModal.vue'
import KeyEventsModal from './conversions/KeyEventsModal.vue'
import AdjustConfigModal from './definitions/AdjustConfigModal.vue'
import KeywordEditModal from './definitions/KeywordEditModal.vue'
import ProductIdMappingModal from './product/ProductIdMappingModal.vue'
import SourceMediumModal from './sourceMedium/SourceMediumModal.vue'

import { useAccount, useWorkspace } from '@/features/account'
import type { ProviderConnectedEvent } from '@/features/integrations'
import { useWorkspaceAttribute } from '@/features/workspace-settings'
import { useLocaleStore } from '@/shared/i18n'

const account = useAccount()
const workspace = useWorkspace()
const locale = useLocaleStore()
const queue = ref<ConnectionFollowup[]>([])
const context = ref<{ accountId: string; workspaceId: string } | null>(null)

const brandedKeywords = useKeywordArea('branded_company')
const sourceMedium = {
  google_ads: useSourceMedium('google_ads'),
  meta_ads: useSourceMedium('meta_ads'),
  tiktok_ads: useSourceMedium('tiktok_ads'),
}
const keyEvents = {
  ga4: useKeyEvents(GA4_KEY_EVENTS),
  adjust: useKeyEvents(MMP_KEY_EVENTS.adjust),
  appsflyer: useKeyEvents(MMP_KEY_EVENTS.appsflyer),
}
const product = useProductCustomization()
const hardal = useHardalEvents()
const adjust = useWorkspaceAttribute('adjust_configurations')

const current = computed(() => queue.value[0] ?? null)
const currentSourcePlatform = computed(() => {
  const followup = current.value
  if (followup?.kind !== 'source-medium') return null
  return sourceMediumPlatformDescriptors.find((item) => item.field === followup.platform) ?? null
})
const currentSourceController = computed(() => {
  const followup = current.value
  return followup?.kind === 'source-medium' ? sourceMedium[followup.platform] : null
})
const currentProductSource = computed<FeedSourceDescriptor | null>(() => {
  const followup = current.value
  return followup?.kind === 'product-id-mapping' ? productFeedSource(followup.platform) : null
})
const currentKeyController = computed(() => {
  const followup = current.value
  return followup?.kind === 'key-events' ? keyEvents[followup.platform] : null
})
const currentKeyAccount = computed<KeyEventAccount | null>(() => {
  const followup = current.value
  const controller = currentKeyController.value
  if (followup?.kind !== 'key-events' || !controller) return null
  return (
    controller.accounts.value.find((item) => item.accountId === followup.accountId) ??
    controller.accounts.value[0] ??
    null
  )
})

function clear(): void {
  queue.value = []
  context.value = null
}

function advance(): void {
  queue.value = queue.value.slice(1)
  if (queue.value.length === 0) context.value = null
}

async function consume(event: ProviderConnectedEvent): Promise<void> {
  const accountId = account.accountId.value
  const workspaceId = workspace.workspaceId.value
  if (accountId === undefined || workspaceId === undefined) return clear()

  await registerDefinitionsMessages(locale.locale)
  const next = resolveConnectionFollowups(event)
  if (next.length === 0) return clear()

  context.value = { accountId: String(accountId), workspaceId: String(workspaceId) }
  queue.value = next
  if (next.some((item) => item.kind === 'product-id-mapping')) void product.load()
  await nextTick()
}

watch([account.accountId, workspace.workspaceId], ([nextAccount, nextWorkspace]) => {
  const scope = context.value
  if (
    scope &&
    (String(nextAccount ?? '') !== scope.accountId ||
      String(nextWorkspace ?? '') !== scope.workspaceId)
  ) {
    clear()
  }
})
watch(
  () => locale.locale,
  (value) => void registerDefinitionsMessages(value),
  { immediate: true },
)

defineExpose({ consume, clear, pending: current })
</script>

<template>
  <div data-testid="connection-followup-host" aria-hidden="true"></div>

  <KeywordEditModal
    v-if="current?.kind === 'branded-keywords'"
    key="branded-keywords"
    title-key="workspaceDefinitions.keywords.brand.title"
    :area="brandedKeywords"
    @close="advance"
  />
  <SourceMediumModal
    v-else-if="
      current?.kind === 'source-medium' && currentSourcePlatform && currentSourceController
    "
    :key="`source-medium:${current.platform}`"
    :platform="currentSourcePlatform"
    :source-medium="currentSourceController"
    @close="advance"
  />
  <ProductIdMappingModal
    v-else-if="current?.kind === 'product-id-mapping' && currentProductSource"
    :key="`product-id:${current.platform}`"
    :source="currentProductSource"
    :controller="product"
    @close="advance"
  />
  <KeyEventsModal
    v-else-if="current?.kind === 'key-events' && currentKeyController && currentKeyAccount"
    :key="`key-events:${current.platform}:${currentKeyAccount.accountId}`"
    :account="currentKeyAccount"
    :controller="currentKeyController"
    @close="advance"
  />
  <AdjustConfigModal
    v-else-if="current?.kind === 'adjust-configuration'"
    key="adjust-configuration"
    :attribute="adjust"
    @close="advance"
  />
  <HardalKeyEventModal
    v-else-if="current?.kind === 'hardal-key-events'"
    key="hardal-key-events"
    :controller="hardal"
    @close="advance"
  />
</template>
